// feature toggle
import { NgRedux } from '@angular-redux/store';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs/Observable';
import { IAppState, Actions } from '../../../app/store';
import {AppAction} from '../../store/interfaces/IAppState';
import { dateDifference, useDefault } from '../../common/common';
import { DateUtils } from '../../common/date.utils';
import { RateRuleDao, ReservationDao, ReservationV2Dao, AvailabilityV2Dao } from '../daos';
import { IHotelReservation } from '../interfaces/HotelReservation/IHotelReservation';
import { IHotelReservationV2 } from '../interfaces/HotelReservationV2/IHotelReservation-IV2';
import { IRateRuleParams } from '../interfaces/RateRuleParameters/IRateRuleParams';
import { IAvailV2SinglePropParams } from '../interfaces/AvailV2SinglePropParameters/IAvailV2SinglePropParams';
import { ReservationValidationError } from '../model/errors';
import { ReservationErrorCodes } from '../model/errors/reservation-error-codes.model';
import { ReservationErrorMessageKeys } from '../model/errors/reservation-error-message-keys.model';
import { ServiceError, ValidationError } from '../model/errors/service-error.model';
import { FeatureFlag } from '../model/featureflags/feature-flag.model';
import { FeatureName } from '../model/featureflags/feature-name.model';
import { Hotel } from '../model/hotel/hotel.model';
import { ReservationChangeType } from '../model/reservation/change/reservation-change.model';
import { ReservationStatusType } from '../model/reservation/reservation-status.model';
import { Reservation } from '../model/reservation/reservation.model';
import { User } from '../model/user/user.model';
import { FeatureFlagsService } from '../services/feature-flags.service';
import { ReservationErrorAdapter } from './adapters/reservation-error.adapter';
import { BaseService } from './base.service';
import { ReservationModelBuilder } from './builders/reservation-model.builder';
import { ReservationRequestBuilder } from './builders/reservation-request.builder';
import { ReservationV2RequestBuilder } from './builders/reservation-v2-request.builder';
import { ReservationV2ModelBuilder } from './builders/reservationV2-model.builder';
import { LogService } from './logging.service';
import { ReservationErrorMapper } from './mapper/reservation-error.mapper';
import { ReservationV2ErrorMapper } from './mapper/reservation-v2-error.mapper';
import { ReservationServiceValidator } from './validators/reservation.validator';
import { ReservationV2ServiceValidator } from './validators/reservationV2.validator';

const RESERVATION_AVAILABILITY_CACHE_DURATION = 10 * 60 * 1000; // 10 mins
const CLASS_NAME = 'ReservationAvailabilityService';
const FREE_NIGHT_MAX_NUMBER_OF_ROOMS = 1;

const SSO_TOKEN_ATTRIBUTE_NAME = 'X-IHG-SSO-TOKEN';

interface AvailabilityChangesId {
  reservation: Reservation,
  user: User,
  changes: Map<ReservationChangeType, any>
}

@Injectable()
export class ReservationAvailabilityService extends BaseService<Reservation, AvailabilityChangesId> {
  private _reservationAvailabilityObservables: Map<string, Observable<Reservation>>;
  private _state: IAppState;
  private _featureFlags: Array<FeatureFlag>;

  constructor(
    private _reservationDao: ReservationDao,
    private _reservationV2Dao: ReservationV2Dao,
    private _rateRulesDao: RateRuleDao,
    private _availabilityV2Dao: AvailabilityV2Dao,
    private _log: LogService,
    private _cookieService: CookieService,
    private _ngRedux: NgRedux<IAppState>,
    private _actions: Actions,
    private _featureFlagsService: FeatureFlagsService
  ) {
    super(new ReservationErrorMapper(), RESERVATION_AVAILABILITY_CACHE_DURATION);
    this._updateFromState();
    this._ngRedux.subscribe(() => {
      this._updateFromState();
    });
    this._reservationAvailabilityObservables = new Map<string, Observable<Reservation>>();
  }

  getReservationAvailability(reservation: Reservation, reservationChanges: Map<ReservationChangeType, any>, user: User, useCache: boolean = false): Observable<Reservation> {
    if (!this.allowedChangesToNumberOfNights(reservation, reservationChanges)) {
      const error = new ReservationValidationError('errorDates' + ReservationErrorCodes.ERROR_FREE_NIGHTS, ReservationErrorMessageKeys.ERROR_FREE_NIGHTS_KEY);
      return Observable.throw(error);
    }
    if (!this.allowedChangesToNumberOfRooms(reservation, reservationChanges)) {
      const error = new ReservationValidationError(ReservationErrorCodes.ERROR_FREE_NIGHTS, ReservationErrorMessageKeys.ERROR_FREE_NIGHTS_ROOMS_KEY);
      return Observable.throw(error);
    }

    return this.getByKey(<AvailabilityChangesId>{reservation: reservation, changes: reservationChanges, user: user}, useCache);
  }

  private allowedChangesToNumberOfNights(reservation: Reservation, reservationChanges: Map<ReservationChangeType, any>) {
    const reservationChangeRequest: IHotelReservation = new ReservationRequestBuilder()
      .withReservation(reservation)
      .withChanges(reservationChanges)
      .build();
    return !(reservation.rate.freeNight && dateDifference(reservationChangeRequest.stay.checkInDate, reservationChangeRequest.stay.checkOutDate) > reservation.stay.numberOfNights);

  }

  private allowedChangesToNumberOfRooms(reservation: Reservation, reservationChanges: Map<ReservationChangeType, any>) {
    const numberOfRooms = reservationChanges.get(ReservationChangeType.NUMBER_OF_ROOMS);
    return !(reservation.rate.freeNight && useDefault(numberOfRooms, reservation.stay.numberOfRooms) > FREE_NIGHT_MAX_NUMBER_OF_ROOMS);
  }

  protected getFreshObservable(availabilityId: AvailabilityChangesId): Observable<Reservation> {
    const useAvailV2Dao = this._featureFlagsService.checkFeatureFlag(FeatureName.AVAILABILITY_V2, '0.1.0', this._featureFlags);
    const reservation: Reservation = availabilityId.reservation;
    const reservationChanges: Map<ReservationChangeType, any> = availabilityId.changes;
    const rateRulesRequest: IRateRuleParams = <IRateRuleParams>{
      hotelCode: reservation.hotelMnemonic,
      rateCode: useDefault(reservationChanges.get(ReservationChangeType.RATE), reservation.rate.code),
      roomCode: useDefault(reservationChanges.get(ReservationChangeType.ROOM), reservation.room.code),
      adults: useDefault(reservationChanges.get(ReservationChangeType.NUMBER_OF_ADULTS), reservation.stay.numberOfAdults),
      children: useDefault(reservationChanges.get(ReservationChangeType.NUMBER_OF_CHILDREN), reservation.stay.numberOfChildren),
      rooms: useDefault(reservationChanges.get(ReservationChangeType.NUMBER_OF_ROOMS), reservation.stay.numberOfRooms),
      dateRange: {
        start: DateUtils.toShortISODate(useDefault(reservationChanges.get(ReservationChangeType.CHECK_IN_DATE), reservation.stay.checkInDate)),
        end: DateUtils.toShortISODate(useDefault(reservationChanges.get(ReservationChangeType.CHECK_OUT_DATE), reservation.stay.checkOutDate))
      }
    };
    const availV2SinglePropRequest: IAvailV2SinglePropParams = <IAvailV2SinglePropParams>{
      startDate: DateUtils.toShortISODate(useDefault(reservationChanges.get(ReservationChangeType.CHECK_IN_DATE), reservation.stay.checkInDate)),
      endDate: DateUtils.toShortISODate(useDefault(reservationChanges.get(ReservationChangeType.CHECK_OUT_DATE), reservation.stay.checkOutDate)),
      hotelMnemonics: [reservation.hotelMnemonic],
      rates: {
          ratePlanCodes: [ {internal: useDefault(reservationChanges.get(ReservationChangeType.RATE), reservation.rate.code)}]
      },
      products: [
          {
              productTypeCode: 'SR',
              productCode: useDefault(reservationChanges.get(ReservationChangeType.ROOM), reservation.room.code),
              adults: useDefault(reservationChanges.get(ReservationChangeType.NUMBER_OF_ADULTS), reservation.stay.numberOfAdults),
              children: useDefault(reservationChanges.get(ReservationChangeType.NUMBER_OF_CHILDREN), reservation.stay.numberOfChildren),
              quantity: useDefault(reservationChanges.get(ReservationChangeType.NUMBER_OF_ROOMS), reservation.stay.numberOfRooms)
          }
      ]
    };
    if (reservation.rate && reservation.rate.groupCode) {
      delete availV2SinglePropRequest.rates.ratePlanCodes;
      availV2SinglePropRequest.rates.groupCode = reservation.rate.groupCode;

    }
    if (!!reservation.corporateId) {
      availV2SinglePropRequest.corporateAccounts = [reservation.corporateId];
    }

    if (useAvailV2Dao) {
      return this._availabilityV2Dao.get(availV2SinglePropRequest)
      .mergeMap((availData: any) => {
        if (!ReservationServiceValidator.validateAvailSinglePropData(availData) && Hotel.isRateChangesNeeded(reservation.hotelMnemonic)) {
          this._log.error(`Not enough Avail data to build reservation availability request for LMS reservation`, CLASS_NAME, availData, availV2SinglePropRequest);
          this._actions.dispatchRateDetailsAction(AppAction.GET_RATE_DETAILS, {});
          throw new ValidationError(availData);
        }
        this._actions.dispatchRateDetailsAction(AppAction.GET_RATE_DETAILS, availData);
        return this._getAvailableReservation(reservation, reservationChanges, availabilityId.user, {}, availData);
      })
      .catch((error) => {
        if (error instanceof ServiceError) {
          return Observable.throw(error);
        }
        const serviceError = new ReservationV2ErrorMapper().map(error);

        this._log.warn(`Error when getting rate rules for reservation availability for: ${reservation.confirmationNumber}`, CLASS_NAME, serviceError);
        if (Hotel.isRateChangesNeeded(reservation.hotelMnemonic)) {
          return Observable.throw(serviceError);
        }
        return this._getAvailableReservation(reservation, reservationChanges, availabilityId.user);
      });
    } else {
      return this._rateRulesDao.get(rateRulesRequest)
      .mergeMap((rateRulesData: any) => {
        if (!ReservationServiceValidator.validateRateRulesData(rateRulesData) && Hotel.isRateChangesNeeded(reservation.hotelMnemonic)) {
          this._log.error(`Not enough Rate Rules data to build reservation availability request for LMS reservation`, CLASS_NAME, rateRulesData, rateRulesRequest);
          throw new ValidationError(rateRulesData);
        }
        return this._getAvailableReservation(reservation, reservationChanges, availabilityId.user, rateRulesData);
      })
      .catch((error) => {
        if (error instanceof ServiceError) {
          return Observable.throw(error);
        }
        const serviceError: ServiceError = this.mapError(error);
        this._log.warn(`Error when getting rate rules for reservation availability for: ${reservation.confirmationNumber}`, CLASS_NAME, serviceError);
        if (Hotel.isRateChangesNeeded(reservation.hotelMnemonic)) {
          return Observable.throw(serviceError);
        }
        return this._getAvailableReservation(reservation, reservationChanges, availabilityId.user);
      });
    }
  }

  protected _getAvailableReservation(reservation: Reservation, reservationChanges: Map<ReservationChangeType, any>, user: User, rateRulesData?: any, availData?: any) {
    const useResV2Dao = this._featureFlagsService.checkFeatureFlag(FeatureName.RESERVATION_V2, '0.1.0', this._featureFlags);
    const reservationWithRateRules: Reservation = new ReservationModelBuilder().withRateRulesData(rateRulesData).build();

    if (useResV2Dao) {
      const reservationV2ChangeRequest: IHotelReservationV2 = new ReservationV2RequestBuilder()
        .withReservation(reservation)
        // todo: .withReservationRateChanges(reservationWithRateRules.rate, reservation.hotelMnemonic)
        .withChanges(reservationChanges)
        .withStatus(ReservationStatusType.PENDING)
        .withIds(reservation)
        .withPayment(reservation, availData)
        .withCommunicationPreferences(reservation)
        .build();
      return this._reservationV2Dao.put(reservation.confirmationNumber, reservationV2ChangeRequest)
        .map((reservationData) => {
          if (!ReservationV2ServiceValidator.validateReservationData(reservationData)) {
            this._log.error(`Not enough Reservation information data to build reservation availability for: ${reservation.confirmationNumber}`, CLASS_NAME, {}, reservationData);
            throw new ValidationError(reservationData);
          }
          if (!ReservationV2ServiceValidator.validateRateRulesData(rateRulesData) && !ReservationV2ServiceValidator.validateAvailSinglePropData(availData)) {
            this._log.error(`Not enough Rate Rules data to build reservation availability for: ${reservation.confirmationNumber}`, CLASS_NAME, {}, rateRulesData);
            throw new ValidationError(rateRulesData);
          }
          return new ReservationV2ModelBuilder()
            .withReservationData(reservationData)
            .build();
        })
        .catch((error) => {
          if (error && error.error && error.error.errors && error.error.errors.length) { //V2 avail error has the useful error in an errors array
            error.error = error.error.errors[0];
          }

          const serviceError = new ReservationV2ErrorMapper().map(error);

          this._log.error(`Error when getting reservation availability ${reservation.confirmationNumber}`, CLASS_NAME, serviceError);
          return Observable.throw(ReservationErrorAdapter.adaptError(serviceError, rateRulesData, user));
        })
    } else {
      const reservationChangeRequest: IHotelReservation = new ReservationRequestBuilder()
        .withReservation(reservation)
        .withSpecialNightRate(reservation.specialNightRate)
        .withReservationRateChanges(reservationWithRateRules.rate, reservation.hotelMnemonic) //todo make sure this is addressed when avail v2 is used
        .withChanges(reservationChanges)
        .withStatus(ReservationStatusType.PENDING)
        .build();
      return this._reservationDao.put(reservationChangeRequest)
        .map((reservationData) => {
          if (!ReservationServiceValidator.validateReservationData(reservationData)) {
            this._log.error(`Not enough Reservation information data to build reservation availability for: ${reservation.confirmationNumber}`, CLASS_NAME, reservationData, reservationChangeRequest);
            throw new ValidationError(reservationData);
          }
          if (!ReservationServiceValidator.validateRateRulesData(rateRulesData)) {
            this._log.error(`Not enough Rate Rules data to build reservation availability for: ${reservation.confirmationNumber}`, CLASS_NAME, rateRulesData);
            throw new ValidationError(rateRulesData);
          }
          return new ReservationModelBuilder()
            .withReservationData(reservationData)
            .withRateRulesData(rateRulesData)
            .withGroupCodeData(reservation, rateRulesData, reservationChanges.get(ReservationChangeType.GROUP_CODE))
            .build();
        })
        .catch((error) => {
          const serviceError: ServiceError = this.mapError(error);
          this._log.warn(`Error when getting reservation availability ${reservation.confirmationNumber}`, CLASS_NAME, serviceError);
          return Observable.throw(ReservationErrorAdapter.adaptError(serviceError, rateRulesData, user));
        })
        .finally(() => {
          const reservationReleaseRequest: IHotelReservation = new ReservationRequestBuilder()
            .withReservation(reservation)
            .withReservationRateChanges(reservation.rate, reservation.hotelMnemonic)
            .withStatus(ReservationStatusType.IGNORE)
            .withSpecialNightRate(reservation.specialNightRate)
            .build();
          this._reservationDao.put(reservationReleaseRequest)
            .subscribe((response) => {
                this._log.info(`Availability successfully released for reservation: ${reservation.confirmationNumber}`, CLASS_NAME, response);
              },
              (error) => {
                this._log.warn(`Error when releasing changes for reservation: ${reservation.confirmationNumber}`, CLASS_NAME, error, reservationReleaseRequest);
              });
        });
    }
  }

  private _updateFromState() {
    this._state = this._ngRedux.getState();
    this._featureFlags = this._state.featureFlags.flags;
  }

  protected generateKey(availabilityId: AvailabilityChangesId): string {
    let generatedKey = useDefault(availabilityId.reservation.confirmationNumber, '');
    availabilityId.changes.forEach((value: any, key: ReservationChangeType) => {
      generatedKey += `${key.toString()}-${value}`;
    });
    generatedKey += useDefault(this._cookieService.get(SSO_TOKEN_ATTRIBUTE_NAME), '');
    return generatedKey;
  }
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/api/services/reservation-availability.service.ts